import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../context/AuthContext';
import DashboardHeader from '../../components/ui/DashboardHeader';
import BotPerformanceCard from '../../components/ui/BotPerformanceCard';
import ActivityFeed from '../../components/ui/ActivityFeed';
import PortfolioKPICard from './components/PortfolioKPICard';
import PortfolioSelector from './components/PortfolioSelector';
import TimeframeToggle from './components/TimeframeToggle';
import ConnectionStatus from './components/ConnectionStatus';
import QuickActions from './components/QuickActions';
import EmptyState from './components/EmptyState';
import vaultsData from '../../data/vaults.json';

const MainDashboard = () => {
  const { user } = useAuth();
  const [selectedPortfolio, setSelectedPortfolio] = useState('all');
  const [activeTimeframe, setActiveTimeframe] = useState('24h');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(new Date()?.toISOString());
  const [isConnected, setIsConnected] = useState(true);

  const portfolios = [
    { label: 'All Vaults', value: 'all' },
    { label: 'MEXC', value: 'mexc' },
    { label: 'Binance', value: 'binance' },
    { label: 'Prediction Futures', value: 'prediction futures' },
    { label: 'Lighter', value: 'lighter' }
  ];

  const timeframes = [
    { label: '24h', value: '24h', icon: 'Clock' },
    { label: '7d', value: '7d', icon: 'Calendar' },
    { label: '30d', value: '30d', icon: 'Calendar' },
    { label: 'All Time', value: 'all', icon: 'Infinity' }
  ];

  // Calculate user's share of portfolio
  const userSharePercent = user?.sharePercent || 0;
  const totalPortfolioValue = vaultsData.portfolioSummary.totalValue;
  const totalProfit = vaultsData.portfolioSummary.totalProfit;
  const overallROI = vaultsData.portfolioSummary.overallROI;

  // Calculate 24h profit (approximate based on operating bot performance:
  // live, paper-testing, and paused bots all count; halted/archived do not)
  const operatingBots = vaultsData.vaults.filter(v => ['active', 'testing', 'paused'].includes(v.status));
  const stoppedVaults = vaultsData.vaults.filter(v => ['halted', 'archived'].includes(v.status));
  const estimated24hProfit = operatingBots.reduce((sum, bot) => {
    return sum + (bot.currentValue * (bot.performance24h / 100));
  }, 0);

  const kpiData = [
    {
      title: 'Total Portfolio Value',
      value: totalPortfolioValue,
      change: estimated24hProfit,
      changePercent: (estimated24hProfit / totalPortfolioValue) * 100,
      icon: 'Wallet',
      iconColor: 'primary',
      trend: [45, 48, 52, 55, 58, 62, 68, 72, 78, 85, 92, 100]
    },
    {
      title: 'Your Shares',
      value: `${user?.shares?.toLocaleString() || 0} / 100,000`,
      change: 0,
      changePercent: 0,
      icon: 'PieChart',
      iconColor: 'accent',
      trend: [userSharePercent, userSharePercent, userSharePercent, userSharePercent, userSharePercent, userSharePercent, userSharePercent, userSharePercent, userSharePercent, userSharePercent, userSharePercent, userSharePercent],
      subtitle: `${userSharePercent}% ownership • $${((totalPortfolioValue * userSharePercent) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} value`
    },
    {
      title: '24h Profit/Loss',
      value: estimated24hProfit,
      change: estimated24hProfit,
      changePercent: (estimated24hProfit / totalPortfolioValue) * 100,
      icon: 'TrendingUp',
      iconColor: 'success',
      trend: [20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75]
    },
    {
      title: 'Operating Vaults',
      value: `${operatingBots.length} / ${vaultsData.vaults.length}`,
      change: 0,
      changePercent: 0,
      icon: 'Bot',
      iconColor: 'accent',
      trend: [2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2, 2],
      subtitle: stoppedVaults.length > 0 ? `${stoppedVaults.length} vault${stoppedVaults.length > 1 ? 's' : ''} halted/archived` : 'All vaults operational'
    },
    {
      title: 'Overall ROI',
      value: `${overallROI.toFixed(2)}%`,
      change: 0,
      changePercent: overallROI,
      icon: 'Target',
      iconColor: 'success',
      trend: [15, 22, 28, 35, 40, 45, 50, 52, 55, 58, 62, 64]
    }
  ];

  // Transform vault data for BotPerformanceCard
  const botPerformanceData = vaultsData.vaults.map(vault => ({
    id: vault.id,
    name: vault.name,
    exchange: vault.exchange,
    status: vault.status,
    statusReason: vault.statusReason,
    winRate: vault.winRate,
    sharpeRatio: vault.sharpeRatio,
    maxDrawdown: vault.maxDrawdown,
    totalTrades: vault.totalTrades,
    profitLoss: vault.totalProfit,
    profitLossPercent: vault.profitPercent,
    lastUpdate: new Date()?.toISOString(),
    performance24h: vault.performance24h,
    performance7d: vault.performance7d,
    performance30d: vault.performance30d,
    activePositions: vault.activePositions,
    currentValue: vault.currentValue,
    initialCapital: vault.initialCapital,
    profitAsset: vault.profitAsset,
    signals: vault.signals,
    usable: vault.usable,
    directionalAccuracy: vault.directionalAccuracy,
    netPnlRaw: vault.netPnlRaw
  }));

  const handleRefresh = () => {
    setIsRefreshing(true);
    setTimeout(() => {
      setIsRefreshing(false);
      setLastUpdate(new Date()?.toISOString());
    }, 2000);
  };

  const handleExport = () => {
    console.log('Exporting dashboard data...');
  };

  const handleSettings = () => {
    console.log('Opening settings...');
  };

  const handlePortfolioChange = (value) => {
    setSelectedPortfolio(value);
  };

  const handleTimeframeChange = (value) => {
    setActiveTimeframe(value);
  };

  useEffect(() => {
    const connectionInterval = setInterval(() => {
      setLastUpdate(new Date()?.toISOString());
    }, 30000);

    return () => clearInterval(connectionInterval);
  }, []);

  const filteredBots = selectedPortfolio === 'all' 
    ? botPerformanceData 
    : botPerformanceData?.filter(bot => bot?.exchange?.toLowerCase() === selectedPortfolio);

  const productionBots = filteredBots?.filter(bot => bot?.status !== 'testing');
  const testingBots = filteredBots?.filter(bot => bot?.status === 'testing');

  return (
    <>
      <Helmet>
        <title>Main Dashboard - TheVault</title>
        <meta name="description" content="Comprehensive trading bot management dashboard with real-time portfolio analytics and performance monitoring" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <DashboardHeader />

        <main className="pt-16">
          <div className="max-w-[1920px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            <div className="mb-6 md:mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 md:gap-6 mb-6 md:mb-8">
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
                    Welcome back, {user?.name || 'Investor'}
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Real-time analytics and performance monitoring for your vault investments
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 md:gap-4">
                  <ConnectionStatus isConnected={isConnected} lastUpdate={lastUpdate} />
                  <QuickActions 
                    onRefresh={handleRefresh}
                    onExport={handleExport}
                    onSettings={handleSettings}
                    isRefreshing={isRefreshing}
                  />
                </div>
              </div>

              <div className="flex flex-col lg:flex-row gap-4 md:gap-6 mb-6 md:mb-8">
                <PortfolioSelector 
                  selectedPortfolio={selectedPortfolio}
                  onPortfolioChange={handlePortfolioChange}
                  portfolios={portfolios}
                />
                <TimeframeToggle 
                  activeTimeframe={activeTimeframe}
                  onTimeframeChange={handleTimeframeChange}
                  timeframes={timeframes}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6">
                {kpiData?.map((kpi, index) => (
                  <PortfolioKPICard
                    key={index}
                    title={kpi?.title}
                    value={kpi?.value}
                    change={kpi?.change}
                    changePercent={kpi?.changePercent}
                    trend={kpi?.trend}
                    icon={kpi?.icon}
                    iconColor={kpi?.iconColor}
                    isLoading={isRefreshing}
                    subtitle={kpi?.subtitle}
                  />
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2">
                <div className="mb-4 md:mb-6">
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                    Vault Performance
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    Individual trading vault analytics and metrics
                  </p>
                </div>

                {productionBots?.length > 0 ? (
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
                    {productionBots?.map((bot, index) => (
                      <BotPerformanceCard key={index} botData={bot} />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    title="No Production Vaults Found"
                    description="There are no production vaults in this category."
                    actionLabel="View All Vaults"
                    onAction={() => setSelectedPortfolio('all')}
                    iconName="Bot"
                  />
                )}

                <div className="mt-8 md:mt-10">
                  <div className="mb-4 md:mb-6">
                    <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground mb-2">
                      Testing Vaults
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Paper-mode and validation-stage bots running signal quality checks
                    </p>
                  </div>
                  {testingBots?.length > 0 ? (
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6">
                      {testingBots?.map((bot, index) => (
                        <BotPerformanceCard key={index} botData={bot} />
                      ))}
                    </div>
                  ) : (
                    <EmptyState
                      title="No Testing Vaults Found"
                      description="There are no testing vaults in this category."
                      actionLabel="View All Vaults"
                      onAction={() => setSelectedPortfolio('all')}
                      iconName="FlaskConical"
                    />
                  )}
                </div>
              </div>

              <div className="lg:col-span-1">
                <ActivityFeed />
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default MainDashboard;
